import { useState, type ChangeEvent } from "react";
import { Button, Input } from "@agentnative/ui";
import { useDevAuthStore } from "../stores/devAuth";
import { useAuthStore } from "../stores/auth";

/**
 * ============================================================================
 * DEV-ONLY AUTH PANEL -- NOT PRODUCTION AUTHENTICATION
 * ============================================================================
 *
 * console now has real authentication (password login/register, HttpOnly
 * session cookies -- see /login, /register, and
 * docs/decisions/ADR-002's follow-on). This panel is a SECONDARY,
 * dev-only escape hatch that remains useful for local development
 * against a console-server started without JWT_SECRET set (a valid,
 * supported local-dev mode -- see console/cmd/console-server/main.go's
 * ephemeral-secret warning), where you may want to exercise org/billing/
 * license pages without going through a full register/login flow for a
 * throwaway local user.
 *
 * This is NOT a login form -- it does not authenticate anyone, does not
 * verify the org/user exists, and does not call any backend endpoint. It
 * is a plain localStorage-backed value, and it now ONLY renders when
 * BOTH of the following hold, so it can never be confused with (or
 * silently override) a real signed-in session:
 *   1. There is no real authenticated user in useAuthStore, AND
 *   2. VITE_ENABLE_DEV_AUTH_PANEL=true is set (opt-in, not on by
 *      default even in dev, so a developer must deliberately enable it).
 * It remains visually flagged (amber banner, "DEV" label) whenever shown.
 */
const DEV_AUTH_PANEL_ENABLED = import.meta.env.VITE_ENABLE_DEV_AUTH_PANEL === "true";

export function DevAuthPanel() {
  const { organizationId, userId, setOrganizationId, setUserId, clear } = useDevAuthStore();
  const realUser = useAuthStore((s) => s.user);
  const [expanded, setExpanded] = useState(false);

  if (!DEV_AUTH_PANEL_ENABLED || realUser) {
    return null;
  }

  return (
    <div className="border-b border-amber-300 bg-amber-50 text-amber-900">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-2 text-xs">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 font-medium uppercase tracking-wide"
        >
          <span className="rounded bg-amber-200 px-1.5 py-0.5 font-semibold">DEV</span>
          Dev Auth Panel -- not real authentication (real login: /login)
          <span className="font-normal normal-case text-amber-700">
            {organizationId ? `acting as org ${organizationId}` : "no org selected"}
          </span>
          <span aria-hidden>{expanded ? "▲" : "▼"}</span>
        </button>
      </div>
      {expanded && (
        <div className="mx-auto max-w-6xl space-y-3 px-6 pb-4 text-sm">
          <p className="text-amber-800">
            console now has real authentication (see /login and /register). This panel is a
            secondary local-dev escape hatch for exercising org/billing/license pages without a
            full login flow, e.g. against a console-server started without JWT_SECRET set. It
            sets the organization_id (and optionally a user_id) used by API calls below. It is
            stored in localStorage on this browser only and is never sent to any auth endpoint.
            It disappears automatically once you are signed in for real.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <Input
              label="organization_id"
              placeholder="org-uuid"
              value={organizationId}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setOrganizationId(e.target.value)}
              className="w-72"
            />
            <Input
              label="user_id (acting as, optional)"
              placeholder="user-uuid"
              value={userId}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setUserId(e.target.value)}
              className="w-72"
            />
            <Button variant="secondary" size="sm" onClick={clear}>
              Clear
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
